import { IMPERIAL, MEASURES, METRIC, regionUnits, UNIT_CHOICES, type Measure, type Units } from '../utils/units';

// The member's units, kept per browser beside the theme (spec 010
// 010 FR-034). A measure without a choice is Automatic and follows the browser's
// region.

const STORAGE_KEY = 'kehai.units';

// automatic is the region's units, fixed for the page's life.
export const automatic: Units = regionUnits(typeof navigator !== 'undefined' ? navigator.language : '');

function readChoice(): Partial<Units> {
  const out: Partial<Units> = {};
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    for (const m of MEASURES) {
      if ((UNIT_CHOICES[m] as string[]).includes(saved?.[m])) (out as Record<Measure, string>)[m] = saved[m];
    }
  } catch {
    // Storage off or unreadable; every measure is Automatic.
  }
  return out;
}

const state = $state<{ choice: Partial<Units> }>({ choice: readChoice() });

// unitsChoice is what the member chose; a measure left out is Automatic.
export function unitsChoice(): Partial<Units> {
  return state.choice;
}

// units is the units each measure shows in. Formatting helpers read it on
// each call, so a template or $derived that formats a value redraws when a
// choice changes.
export function units(): Units {
  return { ...automatic, ...state.choice };
}

// usesMiles is whether distance, pace, and speed show in miles.
export function usesMiles(): boolean {
  return units().distance === 'mi';
}

// setUnit sets one measure's unit, or returns it to Automatic with null.
export function setUnit<M extends Measure>(m: M, unit: Units[M] | null) {
  const next = { ...state.choice };
  if (unit == null) delete next[m];
  else next[m] = unit;
  save(next);
}

// setAllUnits sets every measure to Metric or Imperial.
export function setAllUnits(system: 'metric' | 'imperial') {
  save({ ...(system === 'metric' ? METRIC : IMPERIAL) });
}

function save(choice: Partial<Units>) {
  try {
    if (Object.keys(choice).length) localStorage.setItem(STORAGE_KEY, JSON.stringify(choice));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Not kept past this page when storage is off.
  }
  state.choice = choice;
}
